import { FC, memo, useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { useQueryStates } from "@/contexts/Taxonomy/TaxonomyContext";
import {
  useCompanyFilterActions,
  useCompanyFilterOptions,
  useCompanyFilterSlice,
} from "../../hooks";
import type { RegionId } from "../../types";
import { type GeographyApproach, type GeographySlice } from "./slice";
import type { CountryOption } from "./options";
import { GeoApproachToggle } from "../../components/GeoApproachToggle";
import { GeoCountryCheckboxList } from "../../components/GeoCountryCheckboxList";
import { GeoRegionsSelector } from "../../components/GeoRegionsSelector";
import { GeoStatesCheckboxList } from "../../components/GeoStatesCheckboxList";
import {
  DEFAULT_REGIONS,
  buildUniqueSortedStates,
  countryMatchesSearch,
  toggleExcludedValue,
  toggleIncludedValue,
} from "../../utils/geoSelection";

const EMPTY_COUNTRIES: CountryOption[] = [];

const denseItemSx = {
  display: "flex",
  alignItems: "center",
  ml: 0,
  my: 0,
  "& .MuiRadio-root": { py: 0.25 },
  "& .MuiFormControlLabel-label": { my: 0 },
} as const;

type GeographySelectorProps = {
  value: GeographySlice;
  onChange: (next: GeographySlice) => void;
  /** Prefix for element ids, so two selectors can coexist on a page. */
  idPrefix?: string;
  /**
   * Only the "Include Countries" list, no scope toggle or exclusions — what
   * OppGen's Contact Location offers (GeographyFilterContent countryOnly).
   */
  countryOnly?: boolean;
};

/**
 * The regions / countries / states picker, decoupled from the store so the
 * company Geography filter and the Contact Location filter share it.
 */
export const GeographySelector: FC<GeographySelectorProps> = memo(
  ({ value: slice, onChange, idPrefix = "geography", countryOnly = false }) => {
    const options = useCompanyFilterOptions("geography");

    const [includeSearch, setIncludeSearch] = useState("");
    const [excludeSearch, setExcludeSearch] = useState("");
    const [singleCountrySearch, setSingleCountrySearch] = useState("");
    const [stateSearch, setStateSearch] = useState("");

    const {
      approach: sliceApproach,
      countries: selectedCountries,
      regions: selectedRegions,
      excludedCountries,
      states: selectedStates,
    } = slice;

    const approach = countryOnly ? "countries" : sliceApproach;

    const regions = options?.regions?.length
      ? options.regions
      : DEFAULT_REGIONS;
    const countries = options?.countries ?? EMPTY_COUNTRIES;

    const updateSlice = useCallback(
      (patch: Partial<typeof slice>) => {
        onChange({ ...slice, ...patch });
      },
      [onChange, slice]
    );

    const filteredSingleCountryOptions = useMemo(() => {
      return countries.filter((c) =>
        countryMatchesSearch(c, singleCountrySearch)
      );
    }, [countries, singleCountrySearch]);

    const selectedCountryForStates = useMemo(() => {
      if (approach !== "states") return undefined;
      return selectedCountries.length > 0 ? selectedCountries[0] : undefined;
    }, [approach, selectedCountries]);

    // Fetch states on-demand for the selected country
    const { data: statesData, loading: statesLoading } = useQueryStates(
      selectedCountryForStates
        ? { countryAlpha2: selectedCountryForStates }
        : {}
    );

    const statesForSelectedCountry = useMemo(
      () => buildUniqueSortedStates(statesData),
      [statesData]
    );

    const handleApproachChange = useCallback(
      (next: GeographyApproach) => {
        if (next === approach) return;

        if (next === "regions") {
          updateSlice({
            approach: next,
            countries: [],
            states: [],
          });
          return;
        }

        if (next === "countries") {
          updateSlice({
            approach: next,
            regions: [],
            states: [],
            excludedCountries: excludedCountries.filter(
              (code) => !selectedCountries.includes(code)
            ),
          });
          return;
        }

        // states
        const [existingCountry] = selectedCountries;
        updateSlice({
          approach: next,
          regions: [],
          excludedCountries: [],
          countries: existingCountry ? [existingCountry] : [],
          states: [],
        });
      },
      [approach, excludedCountries, selectedCountries, updateSlice]
    );

    const handleRegionChange = useCallback(
      (regionId: RegionId, checked: boolean) => {
        const newRegions = checked
          ? [...selectedRegions, regionId]
          : selectedRegions.filter((r) => r !== regionId);
        updateSlice({ regions: newRegions });
      },
      [selectedRegions, updateSlice]
    );

    const handleCountryChange = useCallback(
      (countryCode: string, checked: boolean) => {
        const next = toggleIncludedValue(
          selectedCountries,
          excludedCountries,
          countryCode,
          checked
        );
        updateSlice({
          countries: next.included,
          excludedCountries: next.excluded,
        });
      },
      [excludedCountries, selectedCountries, updateSlice]
    );

    const handleExcludeCountryChange = useCallback(
      (countryCode: string, checked: boolean) => {
        const next = toggleExcludedValue(
          selectedCountries,
          excludedCountries,
          countryCode,
          checked
        );
        updateSlice({
          countries: next.included,
          excludedCountries: next.excluded,
        });
      },
      [excludedCountries, selectedCountries, updateSlice]
    );

    const handleSingleCountrySelect = useCallback(
      (countryCode: string) => {
        updateSlice({ countries: [countryCode], states: [] });
      },
      [updateSlice]
    );

    const handleStateToggle = useCallback(
      (stateId: string) => {
        const newStates = selectedStates.includes(stateId)
          ? selectedStates.filter((s) => s !== stateId)
          : [...selectedStates, stateId];
        updateSlice({ states: newStates });
      },
      [selectedStates, updateSlice]
    );

    return (
      <Box
        id={`${idPrefix}-filter-container`}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {!countryOnly && (
          <>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Scope
              </Typography>
              <GeoApproachToggle
                value={approach}
                onChange={handleApproachChange}
              />
            </Box>

            <Divider />
          </>
        )}

        <Box>
          {approach === "regions" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <GeoRegionsSelector
                regions={regions}
                selectedRegionIds={selectedRegions}
                onToggle={handleRegionChange}
              />

              <GeoCountryCheckboxList
                title="Exclude Countries (optional)"
                countries={countries}
                searchValue={excludeSearch}
                onSearchChange={setExcludeSearch}
                selectedCodes={excludedCountries}
                onToggle={handleExcludeCountryChange}
                maxHeight={220}
                searchInputId={`${idPrefix}-regions-exclude-search-input`}
              />
            </Box>
          )}

          {approach === "countries" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <GeoCountryCheckboxList
                title="Include Countries"
                countries={countries}
                searchValue={includeSearch}
                onSearchChange={setIncludeSearch}
                selectedCodes={selectedCountries}
                filterOutCodes={excludedCountries}
                onToggle={handleCountryChange}
                maxHeight={220}
                searchInputId={`${idPrefix}-countries-include-search-input`}
              />

              {!countryOnly && (
                <GeoCountryCheckboxList
                  title="Exclude Countries (optional)"
                  countries={countries}
                  searchValue={excludeSearch}
                  onSearchChange={setExcludeSearch}
                  selectedCodes={excludedCountries}
                  filterOutCodes={selectedCountries}
                  onToggle={handleExcludeCountryChange}
                  maxHeight={220}
                  searchInputId={`${idPrefix}-countries-exclude-search-input`}
                />
              )}
            </Box>
          )}

          {approach === "states" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 0.5 }}
                >
                  Country (choose 1)
                </Typography>
                <TextField
                  id={`${idPrefix}-states-country-search-input`}
                  placeholder="Search countries..."
                  size="small"
                  fullWidth
                  value={singleCountrySearch}
                  onChange={(e) => setSingleCountrySearch(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ mb: 1 }}
                />

                <Box sx={{ maxHeight: 220, overflow: "auto" }}>
                  <RadioGroup
                    value={selectedCountryForStates ?? ""}
                    onChange={(e) => handleSingleCountrySelect(e.target.value)}
                  >
                    {filteredSingleCountryOptions.map((country) => (
                      <FormControlLabel
                        key={country.code}
                        value={country.code}
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            {country.name}
                          </Typography>
                        }
                        sx={{ ...denseItemSx, ml: -1 }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 0.5 }}
                >
                  States / Provinces
                </Typography>

                {!selectedCountryForStates && (
                  <Typography variant="body2" color="text.secondary">
                    Select a country above to filter by states.
                  </Typography>
                )}

                {selectedCountryForStates && (
                  <GeoStatesCheckboxList
                    states={statesForSelectedCountry}
                    selectedStateIds={selectedStates}
                    onToggle={handleStateToggle}
                    searchValue={stateSearch}
                    onSearchChange={setStateSearch}
                    loading={statesLoading}
                    disabled={!selectedCountryForStates}
                    emptyMessage="No states available for the selected country."
                    noResultsMessage="No states found."
                    maxHeight={250}
                    searchInputId={`${idPrefix}-states-search-input`}
                  />
                )}
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 0.5 }}
                >
                  Exclude Countries (optional)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Not available when filtering by states.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

GeographySelector.displayName = "GeographySelector";

export const GeographyFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("geography");
  const { setSlice } = useCompanyFilterActions();
  const handleChange = useCallback(
    (next: GeographySlice) => setSlice("geography", next),
    [setSlice]
  );
  return <GeographySelector value={slice} onChange={handleChange} />;
});

GeographyFilter.displayName = "GeographyFilter";
